import { computed, ComputedRef, inject, provide, ref, Ref } from "vue";
import { AdTemplatePreroll } from "@fresh8/types/src";
import { BackgroundImageSetMap } from "@fresh8/types/src";
import useBackgroundImage from "./useBackgroundImage";

export function usePreroll(
  brandPreroll: ComputedRef<AdTemplatePreroll | undefined>,
  sizeId: ComputedRef<string | undefined>,
  prerollImageSet: ComputedRef<BackgroundImageSetMap | undefined>
): {
  prerollTargetUrl: ComputedRef<string | undefined>;
  usePreroll: ComputedRef<boolean>;
  prerollImage: ComputedRef<string | undefined>;
  prerollName: ComputedRef<string | undefined>;
  prerollDuration: ComputedRef<number | undefined>;
  prerollClicked: Ref<boolean>;
} {
  const prerollImageData = useBackgroundImage(
    sizeId as ComputedRef<string>,
    "preroll",
    prerollImageSet as ComputedRef<BackgroundImageSetMap>
  );

  const usePreroll = computed(
    () =>
      !!brandPreroll.value?.usePreroll &&
      !!prerollImageData.value.backgroundImage
  );

  const prerollClicked = ref(false);

  return {
    prerollTargetUrl: computed(() => brandPreroll?.value?.prerollTargetUrl),
    usePreroll,
    prerollImage: computed(() => prerollImageData.value.backgroundImage),
    prerollName: computed(() => prerollImageData.value.backgroundImageSetName),
    prerollDuration: computed(() => brandPreroll?.value?.prerollDuration ?? 3),
    prerollClicked
  };
}

export type PrerollContext = ReturnType<typeof usePreroll>;
export const brandSymbol = Symbol("prerollSymbol");

export function providePreroll(
  brandPreroll: ComputedRef<AdTemplatePreroll | undefined>,
  sizeId: ComputedRef<string | undefined>,
  prerollImageSet: ComputedRef<BackgroundImageSetMap | undefined>
): void {
  provide(brandSymbol, usePreroll(brandPreroll, sizeId, prerollImageSet));
}

export function injectPreroll(): PrerollContext | undefined {
  return inject<PrerollContext>(brandSymbol);
}
